/* eslint-env browser */
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../ui/button";
import { motion, AnimatePresence } from "framer-motion";
import OptimizedImage from "../../../utils/landing/OptimizedImage";
import { useSmoothNavigate } from "../../../utils/landing/useSmoothNavigate";

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("inicio");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const smoothNavigate = useSmoothNavigate();
  const location = useLocation();

  // Control de scroll para cambiar estilo de navbar
  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== "undefined" && window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Detectar sección activa
      if (typeof document !== "undefined") {
        const sections = document.querySelectorAll("section[id]");
        sections.forEach((section) => {
          const htmlSection = section;
          const sectionTop = htmlSection.offsetTop - 100;
          const sectionHeight = htmlSection.offsetHeight;
          if (
            typeof window !== "undefined" &&
            window.scrollY >= sectionTop &&
            window.scrollY < sectionTop + sectionHeight
          ) {
            setActiveSection(section.getAttribute("id") || "");
          }
        });
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navLinks = [
    { href: "/#sobre-nosotros", label: "Sobre Nosotros" },
    { href: "/#niveles", label: "Niveles" },
    { href: "/#metodologia", label: "Metodología" },
    { href: "/#eventos", label: "Eventos" },
  ];

  const noActiveNav = location.pathname === '/auth' || location.pathname === '/login';

  const navbarClasses = `
  fixed top-0 left-0 right-0 z-50 transition-all duration-300 
  ${
    scrolled
      ? "bg-primary-custom shadow-md border-b-2 border-secondary-custom/20" // Añadido borde inferior
      : "bg-primary-custom"
  }
`;

  return (
    <header className={navbarClasses}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4">
        {/* Logo y título */}
        <a
          href="/#inicio"
          onClick={(e) => smoothNavigate(e, "/#inicio")}
          className="flex items-center gap-3 group transition-all duration-300"
        >
          <div className="relative overflow-hidden rounded-lg bg-white p-1 shadow-md transition-all duration-300 group-hover:scale-105">
            <OptimizedImage
              src="/img/landing/logo.webp"
              alt="Logo IEP San Isidro"
              width={40}
              height={40}
              className="object-contain transition-transform duration-500 group-hover:scale-110"
              priority
            />
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-secondary-custom tracking-tight">
              I.E.P. SAN ISIDRO
            </span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => smoothNavigate(e, link.href)}
              className={`
    px-4 py-2 mx-1 relative text-sm font-medium transition-all duration-300 text-white
    ${
      noActiveNav
        ? 'hover:text-white'
        : activeSection === link.href.replace('/#','')
          ? "font-semibold hover:text-white"
          : "hover:text-yellow-100"
    }
  `}
            >
              {link.label}
              {!noActiveNav && activeSection === link.href.replace('/#','') && (
                <motion.div
                  layoutId="activeSection"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </a>
          ))}

          <Button
            className="ml-6 bg-secondary-custom text-primary-custom font-medium hover:bg-white hover:scale-105 transition-all duration-300 shadow-md"
            onClick={() => navigate("/login")}
          >
            Plataforma Educativa
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-secondary-custom p-2 rounded-full hover:bg-white/10 transition-colors"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-primary-custom/95 backdrop-blur-lg shadow-lg"
          >
            <nav className="flex flex-col space-y-1 p-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMenuOpen(false);
                    setTimeout(() => {
                      smoothNavigate(e, link.href);
                    }, 50);
                  }}
                  className={`
              px-4 py-3 rounded-md transition-all duration-200 text-white
              ${
                noActiveNav
                  ? ''
                  : activeSection === link.href.replace('/#','')
                    ? "bg-white/20 font-semibold"
                    : "hover:bg-white/10"
              }
            `}
                  >
                    {link.label}
                  </a>
                ))}
                <Button
                  className="w-full bg-secondary-custom text-primary-custom font-medium hover:bg-white hover:scale-105 transition-all duration-300 shadow-md"
                  onClick={() => {
                    navigate("/login");
                    setIsMenuOpen(false);
                  }}
                >
                  Plataforma Educativa
                </Button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
    </header>
  );
}
